import NextLink from 'next/link'
import { useMemo } from 'react'

import { type SanityDictionaryEntryPreview } from '@data/sanity/queries/types/dictionary'
import { getLinkPageUrl } from '@lib/routes'

interface DictionaryEntryListProps {
  dictionaryEntries: SanityDictionaryEntryPreview[]
}

const DictionaryEntryList = ({
  dictionaryEntries,
}: DictionaryEntryListProps) => {
  const dictionaryEntriesByLetter = useMemo(() => {
    const dictionaryEntriesForLetter: Record<
      string,
      SanityDictionaryEntryPreview[]
    > = {}

    dictionaryEntries.forEach((dictionaryEntry) => {
      const initialLetter = dictionaryEntry.title[0].toLocaleUpperCase()

      dictionaryEntriesForLetter[initialLetter] = [
        ...(dictionaryEntriesForLetter[initialLetter] ?? []),
        dictionaryEntry,
      ]
    })

    return dictionaryEntriesForLetter
  }, [dictionaryEntries])

  return (
    <section className="container pb-32">
      <div className="flex flex-wrap justify-center items-center gap-x-4">
        {Object.entries(dictionaryEntriesByLetter).map(([letter]) => (
          <a
            key={letter}
            href={`#${letter}`}
            className="flex w-5 h-5 p-4 items-center justify-center text-pageText transition rounded-full hover:bg-pageText hover:text-yellow"
          >
            {letter}
          </a>
        ))}
      </div>

      <div className="">
        {Object.entries(dictionaryEntriesByLetter).map(
          ([letter, dictionaryEntriesForLetter]) => (
            // Use negative margin and padding to scroll into view past the header when clicking letter links
            <div key={letter} id={letter} className="-mt-12 pt-24">
              <h2 className="mb-7 py-2 text-4xl border-b border-green-light">
                {letter}
              </h2>

              <div className="columns-2 md:columns-3 xl:columns-4">
                {dictionaryEntriesForLetter.map(({ slug, title }) => (
                  <div key={slug}>
                    <NextLink
                      href={getLinkPageUrl('dictionaryEntry', slug)}
                      className="inline-block py-1 text-lg leading-normal hover:underline"
                    >
                      {title}
                    </NextLink>
                  </div>
                ))}
              </div>
            </div>
          )
        )}
      </div>
    </section>
  )
}

export default DictionaryEntryList
